import { Control, UseFormWatch } from 'react-hook-form';
import { CustomSlider } from '@/components/ui/custom-slider';
import { defaultHealthcareInflation } from '@shared';
import type { EditAssumptionsFormFields } from '../hooks';

interface InflationRateSectionProps {
  control: Control<EditAssumptionsFormFields>;
  watch: UseFormWatch<EditAssumptionsFormFields>;
  previewClient: Client;
  updatePreviewClient: (updatedClient: Client) => void;
}

export function InflationRateSection({
  control,
  watch,
  previewClient,
  updatePreviewClient,
}: InflationRateSectionProps) {
  const defaultRate = defaultHealthcareInflation(previewClient, true);

  return (
    <div className="space-y-4">
      <h2 className="text-base font-semibold text-gray-900 sm:-mb-2 sm:text-lg">Inflation Rate</h2>

      <div
        className="flex flex-col items-start gap-4 sm:flex-row sm:items-end sm:gap-6"
        data-testid="healthcare-inflation-section"
      >
        <div className="flex shrink-0 items-baseline gap-1 pb-1">
          <span
            className="text-primary-dark text-3xl font-bold sm:text-4xl"
            data-testid="healthcare-inflation-value"
          >
            {parseFloat((Number(watch('healthcareInflation') ?? defaultRate)).toFixed(1))}%
          </span>
        </div>

        <div className="w-full sm:flex-1">
          <CustomSlider
            name="healthcareInflation"
            control={control}
            min={0}
            max={15}
            step={0.1}
            defaultValue={defaultRate}
            labelSuffix="%"
            defaultLabel="Reset to Default"
            formatValue={value => parseFloat(value.toFixed(1))}
            showMinMax
            minMaxSuffix="%"
            data-testid="healthcare-inflation-slider"
            updateClient={value => {
              const updatedClient = {
                ...previewClient,
                clientCalculationSettings: {
                  ...previewClient.clientCalculationSettings,
                  healthCareInflationRate: value / 100,
                },
              };
              updatePreviewClient(updatedClient);
            }}
          />
        </div>
      </div>

      <p className="text-xs italic text-gray-500 sm:text-sm">
        Default rate is {defaultRate}% annually based on CMS Office of the Actuary projections.
      </p>
    </div>
  );
}
